// See documentation: https://github.com/BCoulange/cornis-documentation/wiki/Authentication-Yvon%E2%80%90E
// for more information on the authentication mechanism
// -------------------

import React, { useEffect,useState } from 'react';
import { useSelector,useDispatch } from 'react-redux'
// login for displayed instead of the page if not logged
import LoginForm from './LoginForm.jsx'
import { usersApi } from './app/users.js'
import {useLocation} from "react-router-dom";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

import './AuthenticateUser.css'

// api client
import apiClient from './app/apiClient.js'
import { setUserMetadata, logout, selectUserMetadataLoadedInStore } from './app/auth.js'
import { getBrandAlt, getLoginLogoSrc } from './app/branding.js'

const OUTPUTFIELDS = {
  users: `id,
          name,
          email,
          role,
          projects{
            id,
            name,
            userIds,
            adminIds,
            offices{
              id,
              name,
              missionIds,
              userIds,
              roles
            }
            missions{
              id,
              name,
              inspections{
                id,
                name,
                views{
                  id
                }
              },
              potreeLayers{
                id
              }
            }
          }`
}

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#7487a6"
    }
  }
});

// the component
function AuthenticateUser({children}){
  // State of authentication:
  // possible values: ["restoring_session", "login_needed", "authenticated"]
  // - restoring_session: value at mounting: a /refresh request tries to restore the session
  // - authenticated: the user has been authenticated and apiClient has a valid auth_token
  // - login_needed: the user needs to login
  const [authState, setAuthState] = useState("restoring_session"); 
  // The user id (available as soon as the user is authenticated)
  const [userId, setUserId] = useState(null);
  
  const location = useLocation(); // Location used for user_events
  const userMetadataLoadedInStore = useSelector(selectUserMetadataLoadedInStore) // true when the user metadata (office, etc.) are present in the redux store
  const dispatch = useDispatch()


  // On mounting, listen on the events broadcast channel in order to logout
  useEffect(() => {
    const events_channel = new BroadcastChannel(window.orthophoto_bc_channel + "_events");
    events_channel.onmessage = (event) => {
      if (event.data.type === "logout"){
        setAuthState("login_needed")
        console.log("Clearing Auth")
        apiClient.clearAuth();
      }
    };
  }, [])


  // On mounting: try to restore the session with a /refresh request
  useEffect(() => {
    apiClient.refresh()
    .then((response) => {
      setAuthState("authenticated");
      setUserId(response.user_id)
      return retrieve_user_metadata(response.user_id);
    })
    .catch((error) => {
      setAuthState("login_needed");
    });
  },[]);

  // Function called when the user click on "Sign in" in the LoginForm component
  const login = ({email, password}) => {
    // authenticate
    apiClient.authenticate({
      email,
      password
    })
    .then((response) => {
      setAuthState("authenticated");
      setUserId(response.user_id)
      return retrieve_user_metadata(response.user_id);
    })
    .catch((error) => {
      setAuthState("login_needed");
    }); 
  }


  // UserEvent on page load
  useEffect(() => {
    if (authState === "authenticated"){
      // Make user event
      apiClient.create_user_event({
        userId: userId,
        category: "react_page_access",
        other_infos: {
          url: window.location.href,
          channel: window.orthophoto_bc_channel
        }
      });
    }
    
  }, [location, authState]);

  // Retrieve user metadata from DB
  // and update the Redux store with the metadata and the auth_token
  const retrieve_user_metadata = async (user_id) => {
    usersApi.get(OUTPUTFIELDS.users, ({role, projects, email}) => {
      dispatch(setUserMetadata({
        role,
        projects,
        email,
        auth_token_for_proxy: apiClient.getAuthToken(),
        user_id: user_id
      }));
    });
  }

  
  // Loading page when:
  // - it's trying to restoring the session
  // - or the redux store is not yet updated 
  if (authState === "restoring_session" || (authState === "authenticated" && !userMetadataLoadedInStore)){
    return <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
          <Box
            sx={{
              marginTop: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                height: 100,
                maxWidth: '100%',
              }}
              component="img"
              alt={getBrandAlt()}
              src={getLoginLogoSrc()}
              className="brand-logo-login"
            />
            <Box sx={{
              marginTop: 8
            }}><CircularProgress size={100}/></Box>
          </Box>
      </Container>
    </ThemeProvider>
  }
  // Logged
  else if (authState === "authenticated" && userMetadataLoadedInStore){
    return <div className="auth-container">{children}</div>
  }
  // Else: login needed
  else {
    return <LoginForm login={login}/>
  }
}

export default AuthenticateUser;